import React from 'react';
import { Tab, styled, Tooltip, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import IconReusable from '../../../components/IconReusable';

// Define color tokens
const getColorTokens = (variant) => {
  const colors = {
    primary: {
      text: '#333333',
      border: '#995A00',
      background: '#995A001F',
      hoverBg: '#995A000D',
      disabledText: '#D3C3B1',
      activeText: '#995A00',
    },
    secondary: {
      text: '#333333',
      border: '#333333',
      background: '#F4F4F4',
      hoverBg: '#E0E0E0',
      disabledText: '#AAAAAA',
      activeText: '#995A00',
    },
  };
  return colors[variant] || colors.primary;
};

const getTypeStyles = (type, colorTokens, active, disabled) => {
  const baseColor = disabled
    ? colorTokens.disabledText
    : active
    ? colorTokens.activeText
    : colorTokens.text;
    
  switch (type) {
    case 'boxed':
      return {
        // border: active ? `1px solid ${colorTokens.border}` : `1px solid transparent`,
        backgroundColor: active ? colorTokens.background : 'transparent',
        color: baseColor,
      };
    case 'dashed':
      return {
        border: active
          ? `1px dashed ${colorTokens.border}`
          : `1px dashed transparent`,
        backgroundColor: active ? colorTokens.background : 'transparent',
        color: baseColor,
      };
    case 'standard':
        return {
            color: baseColor,
            backgroundColor: active ? colorTokens.background : 'transparent',
        };
    default:
      return {
        color: baseColor,
        backgroundColor: active ? colorTokens.background : 'transparent',
      };
  }
};
const StyledTab = styled(Tab)(({ theme, ownerState }) => {
  const { variant, type, active, disabled, size } = ownerState;
  const colorTokens = getColorTokens(variant);
  const typeStyles = getTypeStyles(type, colorTokens, active, disabled);
  
  return {
    borderRadius: theme.shape.borderRadius,
    justifyContent: 'center',
    gap: 8,
    minWidth: 'auto',
    fontSize: { xs: '13px', md: '16px' },
    lineHeight : { xs: '20px', md: '24px' },
    fontWeight: 600,
    padding: size === 'small' ? '4px 12px !important' : '8px 24px !important' ,
    minHeight: size === 'small' ? '32px !important' : '40px',   
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    textTransform: 'none',
    ...typeStyles,
    '&.MuiTab-root': {
      color: '#333333',
      padding: '12px 16px',
      fontWeight: 600,
      [theme.breakpoints.up('sm')]: {
        padding: '1px 24px',
      },
      minHeight: '40px',
    },
    '&.Mui-selected': {
      color: '#995A00',
    },
    '& .MuiTab-iconWrapper': {
      marginBottom: 0,
    },
  };
});

const CustomTab = ({
  label,
  icon,
  active = false,
  disabled = false,
  variant = 'primary',
  type = 'standard',
  tooltipText = '',
  size = 'medium',
  ...rest
}) => {
  // Create a custom label that includes both the original label and an info icon with tooltip if tooltipText is provided
  const CustomLabel = () => {
    if (!tooltipText) {
      return label;
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Tooltip 
          title={tooltipText} 
          arrow 
          placement="top"
          enterTouchDelay={0}
          leaveTouchDelay={1500}
          disableFocusListener={false}
          disableTouchListener={false}
          disableHoverListener={false}
        >

          <IconReusable sx={{ cursor: 'pointer'}} name='InfoOutlined' size={'24px'} color={active ? '#995A00' : 'inherit'} />
        </Tooltip>
        {label}
      </Box>
    );
  };

  return (
    <StyledTab
      label={<CustomLabel />}
      icon={icon}
      iconPosition="start"
      disabled={disabled}
      ownerState={{ variant, type, active, disabled, size }}
      {...rest}
    />
  );
};

export default CustomTab;
